import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { openCustomerPortal } from '../../../utils/cn';

const HeroSection = () => {
  const navigate = useNavigate();

  const handleHowItWorks = () => {
    const howItWorksSection = document.getElementById('how-it-works');
    if (howItWorksSection) {
      howItWorksSection?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/how-it-works');
    }
  };

  const handleGetQuote = () => {
    openCustomerPortal();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 90, damping: 18 },
    },
  };

  return (
    <section className="relative min-h-screen overflow-hidden flex items-center">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="/assets/images/pexels-binyaminmellish-1396122_1_-1774979428100.jpg"
          alt="Beautiful residential home with new roof"
          className="w-full h-full object-cover object-center"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        />
        {/* Strong left-side overlay for text legibility, fades on right (lighter on desktop where text is left-column only) */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/92 via-slate-900/85 to-slate-950/40 lg:via-slate-900/70 lg:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/25 to-slate-950/20 lg:from-slate-950/60 lg:via-transparent" />
      </div>
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full pt-32 pb-20 lg:pt-36 lg:pb-24">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          {/* Left Column */}
          <div className="space-y-7">
            {/* Eyebrow */}
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-cyan-300 text-xs font-semibold tracking-widest uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                About OpenbooQ
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-5xl xl:text-[3.5rem] font-bold leading-[1.1] text-white"
            >
              An open-book approach to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400">
                transparent roofing
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={itemVariants}
              className="text-lg text-slate-200 lg:text-slate-300 leading-relaxed max-w-lg"
            >
              OpenbooQ makes roof pricing transparent — material cost plus standardized installation, with no sales pressure and no surprise markups.
            </motion.p>

            {/* Bullets */}
            <motion.div variants={itemVariants} className="space-y-3.5 pt-1">
              {[
                'Transparent, line-item pricing',
                'Vetted installers + warranty-backed work',
                'Fast quotes — no in-home sales pitch',
              ]?.map((bullet, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="text-cyan-400 flex-shrink-0" size={18} strokeWidth={2.5} />
                  <span className="text-white/90 font-medium text-base">{bullet}</span>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3 pt-3">
              <button
                onClick={handleHowItWorks}
                className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold text-base rounded-xl transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/35 hover:-translate-y-0.5"
              >
                How OpenbooQ Works
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
              </button>
              <button
                onClick={handleGetQuote}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/25 text-white font-semibold text-base rounded-xl hover:bg-white/20 hover:border-white/40 transition-all duration-200 hover:-translate-y-0.5"
              >
                Get Your Quote in 30 Minutes
              </button>
            </motion.div>

            {/* Trust line */}
            <motion.div variants={itemVariants} className="flex items-center gap-4 pt-1">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-sm text-slate-400">No obligation</span>
              </div>
              <span className="text-slate-600">·</span>
              <span className="text-sm text-slate-400">30-minute results</span>
              <span className="text-slate-600">·</span>
              <span className="text-sm text-slate-400">100% free</span>
            </motion.div>
          </div>

          {/* Right Column — Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20">
              <img
                src="/assets/images/Gemini_Generated_Image_nq4js8nq4js8nq4j-1774979232497.jpg"
                alt="Two professionals in a modern conference room discussing roofing solutions at OpenbooQ"
                className="w-full object-cover"
                style={{ maxHeight: '540px', filter: 'brightness(1.15) contrast(1.05) saturate(1.05)' }}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;